import { Skeleton } from "@/surfaces/stats/Skeleton";
import type { DayProgress } from "@/lib/review";

import { dayBars, dayFraction } from "./sessionRules";

/**
 * The two day bars — frame `1a` (日本-389).
 *
 * **One component, three places**: the session header, the finished screen's header, and every deck
 * card on the landing. They are the same two measures wherever they are drawn, and building them
 * once is what stops the landing and the sitting from disagreeing about the same day.
 *
 * It knows nothing about a sitting and fetches nothing. Everything it draws comes off `day`, which
 * the server derives on each read — see `lib/review.ts`.
 *
 * **The two bars answer two different questions, which is why one bar could never work.**
 * `カード` is how much of today is left: monotonic, the number you plan an evening around, and the
 * only figure that survives closing the tab and coming back after dinner. `復習` is what that is
 * costing: it moves faster, it moves on failures too, and it is the one that says a session is
 * going badly while there is still time to notice.
 *
 * ## The sizes are the design's, raised to the app's floor
 *
 * The handoff draws the labels and notes at 13px. `styles/index.css` puts the floor at **14px** and
 * says why at length — the line was measured between 13 and 14 on Alex's own monitor — and its rule
 * is that the single exception is `Rail.tsx`'s due badge, because that is digits under a hard width
 * ceiling. Everything here carries kana, so everything here is on the floor. The slot widths grew
 * with it: the note is 104px because `残り55枚` and `正解88.6%` both fit it at 14px, and the notice
 * slot is 140px rather than the handoff's 132 for the same reason.
 *
 * ## Why the fills are flex children
 *
 * The track is a flex row and the two fills are ordinary children with percentage widths — no
 * absolute positioning. That is the handoff's own instruction and it is what makes the 復習 bar's
 * pass and fail sit *adjacent* rather than stacked, so the boundary between them is the day's
 * accuracy read directly off the bar.
 */

interface Props {
  day: DayProgress;
  /** The カード row's note — usually `残り55枚`, but `記録できませんでした` outranks it. */
  cardNote: string;
  /** Whether that note is the failure notice. Told rather than sniffed: it decides the slot's
   *  width and makes it a live region, and neither follows from the text. */
  cardNoteUrgent?: boolean;
  /** The 復習 row's note: the pass/fail tally, or a string the countdown/accuracy has claimed. */
  reviewNote: { passes: number; fails: number } | string;
  /** Whether that note is the idle countdown. Keeps `review.countdown` findable now that it shares
   *  a slot with the day's tally instead of having a corner of its own. */
  reviewNoteCountdown?: boolean;
  /**
   * Drop the `カード` / `復習` labels and tighten everything.
   *
   * **The labels go, the bars do not.** Position carries the meaning — top is cards, bottom is
   * reviews, the same order as the desktop — and the bone/vermilion split is what makes the second
   * bar self-identifying: the top bar is always one flat fill and the bottom one is always two.
   */
  compact?: boolean;
}

export function DayBars({
  day,
  cardNote,
  cardNoteUrgent = false,
  reviewNote,
  reviewNoteCountdown = false,
  compact = false,
}: Props) {
  const fills = dayBars(day);
  return (
    <div
      data-check="review.day"
      className={`flex min-w-0 flex-1 flex-col ${compact ? "gap-[7px]" : "gap-[10px]"}`}
    >
      <Row
        label="カード"
        check="cards"
        compact={compact}
        fraction={dayFraction(day.cardsDone, day.cardsTotal)}
        note={cardNote}
        urgent={cardNoteUrgent}
        fills={[{ width: fills.cards, className: "bg-ink-3", check: "fill" }]}
      />
      <Row
        label="復習"
        check="reviews"
        compact={compact}
        fraction={dayFraction(day.passes + day.fails, day.reviewsTotal)}
        note={reviewNote}
        countdown={reviewNoteCountdown}
        // Order matters: passes first, so the vermilion sits against the empty track and the
        // boundary between the two is where the day's accuracy is.
        fills={[
          { width: fills.pass, className: "bg-ink", check: "pass" },
          { width: fills.fail, className: "bg-accent", check: "fail" },
        ]}
      />
    </div>
  );
}

/**
 * The compact bars, waiting for a day that has just changed under them (日本-404).
 *
 * **Here rather than in `Landing.tsx`, because it is geometry.** It mirrors `Row`'s compact
 * measurements exactly — two `6px` tracks, `gap-[7px]` between them, a `62px` figure column — so the
 * deck card does not reflow when the real bars arrive. Written beside the thing it stands in for is
 * the only way those numbers stay in step; a copy one directory away goes stale the first time a
 * bar's height is touched, and does it silently, since both render fine.
 *
 * **The row's height is the FRACTION's, not the track's**, which is the one measurement that is not
 * self-evident and the one this got wrong first. `Row` is `items-center` over a `6px` track and a
 * `text-meta leading-none` figure, so a compact row is **14px** and the track is the shorter child;
 * built from the track alone the skeleton was 10px a row and the card grew 8px the moment the real
 * bars landed — exactly the reflow this component exists to prevent. `h-[14px]` states it, so the
 * placeholder block stays 10px and the box it sits in does not move.
 *
 * Flat bands, never partial fills. A track drawn part-full is a measurement, and a made-up one is
 * indistinguishable at a glance from the real day — `Skeleton.tsx`'s rule, and the reason this
 * screen was worth fixing in the first place.
 */
export function DayBarsSkeleton() {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-[7px]" aria-hidden>
      {[0, 1].map((row) => (
        <div key={row} className="flex h-[14px] items-center gap-2">
          <span className="flex flex-1">
            <Skeleton height={6} className="rounded-[var(--r-3)]" />
          </span>
          <Skeleton height={10} width={62} />
        </div>
      ))}
    </div>
  );
}

function Row({
  label,
  check,
  compact,
  fraction,
  note,
  urgent = false,
  countdown = false,
  fills,
}: {
  label: string;
  check: "cards" | "reviews";
  compact: boolean;
  fraction: string;
  note: { passes: number; fails: number } | string;
  urgent?: boolean;
  countdown?: boolean;
  fills: Array<{ width: number; className: string; check: string }>;
}) {
  return (
    <div className={`flex items-center ${compact ? "gap-2" : "gap-3"}`}>
      {!compact && (
        <span className="w-11 flex-shrink-0 text-meta leading-none text-ink-3">{label}</span>
      )}
      <span
        className={`flex flex-1 overflow-hidden bg-line ${
          compact ? "h-[6px] rounded-[var(--r-3)]" : "h-[7px] rounded-[var(--r-4)]"
        }`}
      >
        {fills.map((fill) => (
          <span
            key={fill.check}
            data-check={`review.day.${check}.${fill.check}`}
            className={`${fill.className} transition-[width] duration-200 ease-out`}
            style={{ width: `${Math.round(fill.width * 1000) / 10}%` }}
          />
        ))}
      </span>
      <span
        data-check={`review.day.${check}.fraction`}
        className={`flex-shrink-0 whitespace-nowrap text-right font-mono text-meta leading-none tabular-nums ${
          compact ? "w-[62px] text-ink-2" : "w-[88px] text-ink"
        }`}
      >
        {fraction}
      </span>
      {/* The note slot is desktop-only. On a deck card and on the phone the row is the fraction and
          the bar, because 390px has no room for a third column and the fraction is the figure you
          actually read. */}
      {!compact && (
        <span
          data-check={
            urgent ? "review.notice" : countdown ? "review.countdown" : `review.day.${check}.note`
          }
          role={urgent ? "status" : undefined}
          className={`flex-shrink-0 whitespace-nowrap text-right leading-none ${
            countdown ? "font-mono text-meta text-accent-hi" : "text-meta"
          } ${urgent ? "w-[140px] text-accent-hi" : countdown ? "w-[104px]" : "w-[104px] text-ink-4"}`}
        >
          {typeof note === "string" ? (
            note
          ) : (
            <>
              {/* Two inks rather than one string: the passes are body ink and the fails are the
                  accent, which is the same relationship 不可 has to 合格 two rows down. */}
              <span className="text-ink-2">{note.passes}</span>
              <span className="text-ink-4"> · </span>
              <span className="text-accent-text">{note.fails}</span>
            </>
          )}
        </span>
      )}
    </div>
  );
}
